import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AsyncHandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {wrapActionCreator} from '../../../../helpers/reduxHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {TagModel} from '../../../../models/api/tagModel';
import {BulkActionsTrackingTypesEnum} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {Dispatch} from '../../../../store/appStore';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {
  addTagToConversationsAction,
  bulkAddTagToConversationsAction,
} from '../../../../store/data/conversations/tagConversationsActions';
import {toggleHideTagAction} from '../../../../store/data/preferences/preferencesActions';
import {createTagAction, pinTagToTeammateWorkspacesAction} from '../../../../store/data/tags/saveTagActions';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {CreateConversationTagDropdown} from './createConversationTagDropdown';
import {TagTemplate} from './tagEditorBody';

/*
 * Props.
 */

interface CreateConversationTagDropdownContainerProps {
  panelId: ViewerPanelId;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  cellsContext: CellsContext;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
}

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: CreateConversationTagDropdownContainerProps) {
  return props.conversations;
}

function makeGetConversationsNamespace() {
  return createSelector(
    [getUserNamespace, getConversations, getHasPermission],
    (userNamespace, conversations, hasPermission) => {
      const conversationNamespaces = _(conversations)
        .map((c) => c.namespace)
        .uniq()
        .value();

      // If we have different namespaces, use our user's.
      if (conversationNamespaces.length !== 1) {
        return userNamespace;
      }

      // Teammate namespace: always use our user's.
      const conversationNamespace = conversationNamespaces[0];
      if (isTeammateNamespace(conversationNamespace) || conversationNamespace === 'global') {
        return userNamespace;
      }

      // Team namespace: check that we have permission.
      if (!hasPermission(conversationNamespace, PermissionsEnum.EDIT_TAG)) {
        return userNamespace;
      }

      return conversationNamespace;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CreateConversationTagDropdownContainerProps>()(() => ({
  conversationsNamespace: makeGetConversationsNamespace(),
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    createTagAction: wrapActionCreator(dispatch, createTagAction),
    addTagToConversationsAction: wrapActionCreator(dispatch, addTagToConversationsAction),
    bulkAddTagToConversationsAction: wrapActionCreator(dispatch, bulkAddTagToConversationsAction),
    toggleHideTagAction: wrapActionCreator(dispatch, toggleHideTagAction),
    pinTagToTeammateWorkspacesAction: wrapActionCreator(dispatch, pinTagToTeammateWorkspacesAction),
  };
}

export const CreateConversationTagDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {conversations, isBulkActionRequired, inboxesParameters, cellsContext, onInterfaceEvent} = ownProps;

    const addTagToConversations = (tag: TagModel) => {
      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.TAG, isBulkActionRequired);
      }

      if (isBulkActionRequired) {
        bulkAddTagToConversations(tag);
        return;
      }

      normalAddTagToConversations(tag);
    };

    const normalAddTagToConversations = (tag: TagModel) => {
      const conversationIds = conversations.map((c) => c.id);
      dispatchProps.addTagToConversationsAction(conversationIds, tag.id, [tag]);
    };

    const bulkAddTagToConversations = (tag: TagModel) => {
      dispatchProps.bulkAddTagToConversationsAction(inboxesParameters, cellsContext, tag.id, [tag]);
    };

    const createTagAsync: AsyncHandlerOf<TagTemplate> = async (template) => {
      const newTag = await dispatchProps.createTagAction(template);
      addTagToConversations(newTag);

      dispatchProps.toggleHideTagAction(newTag.id, [newTag], {value: template.showInWorkspaces});

      if (template.teammatesToPin.length) {
        dispatchProps.pinTagToTeammateWorkspacesAction(template.teammatesToPin, newTag.id);
      }
    };

    return {
      ...ownProps,
      ...stateProps,
      createTagAsync,
    };
  },
)(CreateConversationTagDropdown);
